import { Injectable } from '@angular/core';
import AuthenticationFacade from '@app/core/facades/authentication.facade';
import { GradeLevel } from '@app/core/models/enumerations/grade-level.enum';
import { MapGradeLevel } from '@app/core/models/enumerations/map-grade-level.enum';
import { StudentService } from '@app/core/services/student/student.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, take, tap } from 'rxjs/operators';
import { GetStudentOutput, GetStudentPlacementGrade } from '../../API.service';

@Injectable()
export class StudentState {
  private readonly _student$ = new BehaviorSubject<GetStudentOutput | null>(null);

  student$ = this._student$.asObservable();

  constructor(private readonly authFacade: AuthenticationFacade, private readonly studentService: StudentService) {
    this.authFacade.studentId$
      .pipe(
        filter((studentId: string) => !!studentId),
        distinctUntilChanged(),
        switchMap((studentId: string) => this.studentService.getStudent(studentId, true))
      )
      .subscribe((student: GetStudentOutput) => this._student$.next(student));
  }

  getCurrentStudent(): Observable<GetStudentOutput> {
    return this.authFacade.studentId$.pipe(
      filter((studentId: string) => !!studentId),
      take(1),
      switchMap((studentId: string) => this.studentService.getStudent(studentId, true)),
      tap((student: GetStudentOutput) => this._student$.next(student))
    );
  }

  setStudentPreferredName(preferredName: string): void {
    if (this._student$.value) {
      this._student$.next({ ...this._student$.value, preferred_name: preferredName });
    }
  }

  updateStudentMapGradeLevel(platformStudentId: string, mapGradeLevel: MapGradeLevel): Observable<void> {
    return this.studentService.updateStudentMapGradeLevel(platformStudentId, mapGradeLevel).pipe(
      tap(() => {
        if (this._student$.value) {
          this._student$.next({ ...this._student$.value, map_grade_level: mapGradeLevel });
        }
      })
    );
  }

  updateStudentPlacementGradeForSubject(
    platformStudentId: string,
    subject: string,
    placementGrade: GradeLevel
  ): Observable<void> {
    return this.studentService.updateStudentPlacementGradeForSubject(platformStudentId, subject, placementGrade).pipe(
      tap(() => {
        if (this._student$.value && this._student$.value.placement_grades) {
          const index: number = this._student$.value.placement_grades.findIndex(
            (grade: GetStudentPlacementGrade) => grade.subject === subject
          );
          if (index !== -1) {
            const placementGrades: GetStudentPlacementGrade[] = [...this._student$.value.placement_grades];
            placementGrades[index].grade = placementGrade;
            this._student$.next({ ...this._student$.value, placement_grades: placementGrades });
          }
        }
      })
    );
  }
}
